import React, { useState, useRef } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import Head from '../components/head';
import { Pagination, SearchBar, HorizontalTabs, Tab } from 'edcom-ui';
import {
  SearchTitle,
  ContextLinks,
  SearchError,
  SearchFooter,
  SearchFootnote,
  SearchFilters,
  SearchResults,
  DidYouMean,
} from '../components/search';
import content, { replaceParams } from '../services/content';
import styles from './search.module.less';
import clsx from 'clsx';
import track, { trackGA } from '../services/track';
import AppLink, { pushAppRoute } from '../components/links/AppLink';
import { CollectionsProvider } from '../services/CollectionsContext';
import AdminLinks from '../components/AdminLinks/AdminLinks';

// EDENG-1220 - Teacher Appreciation Week
// EDENG-2065
const TAWDates = {
  begin: new Date('5/1/2023'),
  end: new Date('5/15/2023'),
};

const TAWIDs = ['175730', '175731', '175732'];

const placeholders = {
  activity: 'activities',
  'science-fair': 'science projects',
  'lesson-plans': 'lesson plans',
  'guided-lessons': 'guided lessons',
};

const searchTrackingFacetMap = {
  contentType: 'types',
  grade: 'grades',
  el: 'el',
  subject: 'subjects',
  topic: 'topics',
  standard: 'standards',
};

const searchBarPlaceholder = filter => {
  const type =
    placeholders[filter.contentType] ?? filter?.contentType ?? 'entire library';
  return `Search ${type}`;
};

const getContentTypes = function getContentTypes(facets = []) {
  facets = facets.filter(facet => facet.internalName === 'contentType');
  return facets.length > 0 ? facets[0].children : facets;
};

const getFacets = function getFacets(facets = []) {
  return facets.filter(facet => facet.internalName !== 'contentType');
};

// Get the URL of the Root of the given facetUrl (e.g. 'grammar' returns 'ela'). This function is relatively fast since
// the allFacets array should only have the children that are rendered in the page so the DFS is quite efficient!
const getRootFacet = function getRootFacet(
  facetUrl = null,
  allFacets = [],
  facetType = 'subject'
) {
  if (!facetUrl || !allFacets || allFacets.length === 0) return null;

  // Filter facets to get the specific facetType
  let facets = allFacets.filter(facet => facet.internalName === facetType);
  facets = facets.length > 0 ? facets[0].children : facets;

  // Initialize the queue to be an array of arrays of form [(Root Facet), (Facet)]
  let queue = [];
  for (var fc of Object.keys(facets)) {
    queue.push([facets[fc], facets[fc]]);
  }
  // Run DFS keeping track of the RootURL of each facet. If we find the facet with urlName=facetUrl, return the Root.
  let first = null;
  while (queue.length > 0) {
    first = queue.shift();
    if (first[1]?.['urlName'] === facetUrl) return first[0];
    if (first[1]?.['children']) {
      first[1]['children'].forEach(child => queue.push([first[0], child]));
    }
  }
  return null;
};

let searchTrigger = null;

export default function Search({
  total = 0,
  pages = 0,
  urlPath,
  meta,
  filter = {},
  currentFilters = [],
  facets = [],
  results = [],
  suggestions = [],
  replaceUrl = false,
  error = false,
}) {
  const router = useRouter();
  const { asPath } = router;
  const [selected, setSelected] = useState([]);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [multiSelectTracked, setMultiSelectTracked] = useState(false);
  const rootRef = useRef(null);

  const handleSelected = selected => {
    // if no selected, make sure it's view-all
    if (selected.length === 0) {
      setShowOnlySelected(false);
    } else if (selected.length > 1 && !multiSelectTracked) {
      trackGA('Search', 'Multiple items selected');
      setMultiSelectTracked(true);
    }
    setSelected(selected);
  };

  // Update URL if search response returned a different url
  React.useEffect(() => {
    if (replaceUrl) {
      router.replace('/[...search]', urlPath);
    }
    handleSelected(selected);
    setShowOnlySelected(false);
  }, [replaceUrl, asPath]);

  // Track search
  React.useEffect(() => {
    if (error || replaceUrl) {
      return;
    }

    let logEvent = searchTrigger;
    searchTrigger = null;

    if (logEvent === 'pagination') {
      return;
    }
    if (!logEvent) {
      let cid = location.search.match(/[?&]cid=([0-9.]+)/);
      cid = cid ? cid[1] : null;
      if (cid === '11.74') {
        logEvent = 'topnav';
      } else if (cid === '11.75') {
        logEvent = 'emphasized-topnav';
      } else {
        logEvent = 'pageview';
      }
    }

    const data = {
      nextjs: true,
      num_results: total,
      didyoumean: !!suggestions.length,
      trigger: logEvent,
      q: filter.q,
      page: filter.page,
      sort: filter.sort,
    };

    facets.forEach(({ internalName }) => {
      if (filter[internalName]) {
        data[searchTrackingFacetMap[internalName]] = JSON.stringify([
          filter[internalName],
        ]);
      } else {
        data[searchTrackingFacetMap[internalName]] = '[]';
      }
    });

    track('Search', data);
  }, [urlPath, total, suggestions, filter]);

  const handleClickContentType = async (e, href) => {
    e.preventDefault();
    searchTrigger = 'filter';
    await pushAppRoute(router, href);
  };

  const handleSearch = async term => {
    const newUrl = replaceParams(urlPath, { q: term });
    searchTrigger = 'autocomplete';
    await pushAppRoute(router, newUrl);
  };

  const generatePaginatedUrl = page => {
    return replaceParams(urlPath, { page: page > 1 ? page : null });
  };

  const handlePagination = (e, page) => {
    e.preventDefault();
    const href = replaceParams(urlPath, { page: parseInt(page) });
    searchTrigger = 'pagination';
    pushAppRoute(router, href).then();
    if (rootRef.current != null) {
      rootRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleClickSearchLink = async href => {
    if (href == null) return;

    href = replaceParams(href, { page: null });
    searchTrigger = 'filter';
    await pushAppRoute(router, href);
  };

  const handleClickSuggestion = async term => {
    const href = replaceParams(urlPath, { q: term });
    searchTrigger = 'didyoumean';
    await pushAppRoute(router, href);
  };

  const contentTypes = getContentTypes(facets);

  let resultShown = showOnlySelected ? selected : results;
  let pagesShown = showOnlySelected ? 1 : pages;

  // EDENG-1220 - Teacher Appreciation Week
  const date = new Date();
  const time = date.getTime();

  if (time >= TAWDates.begin && time < TAWDates.end) {
    resultShown.forEach(function(result, index) {
      if (
        resultShown[index]['type'] === 'workbook' &&
        TAWIDs.includes(resultShown[index]['id'])
      ) {
        resultShown[index]['premium'] = false;
      }
    });
  }

  return (
    <div>
      <Head
        description={meta.metaDescription}
        index={meta.index}
        path={urlPath}
      >
        <title>{meta.metaTitle}</title>
        {filter.page * 1 === 2 && (
          <link rel="prev" href={replaceParams(urlPath, { page: null })} />
        )}
        {filter.page > 2 && (
          <link
            rel="prev"
            href={replaceParams(urlPath, { page: filter.page - 1 })}
          />
        )}
        {filter.page < pagesShown && (
          <link
            rel="next"
            href={replaceParams(urlPath, { page: filter.page * 1 + 1 })}
          />
        )}
      </Head>
      <div id="unified-search-results" ref={rootRef}>
        <div className={styles.searchResults}>
          <SearchTitle
            title={meta.title}
            contextualLinksTitle={meta.contextualLinksTitle || ''}
            contextualLinks={meta.contextualLinks || []}
          >
            <SearchBar
              className={styles.search}
              title={meta.title}
              onSubmit={handleSearch}
              placeholder={searchBarPlaceholder(filter)}
            />
            {meta.contextualLinks && meta.contextualLinks.length > 0 && (
              <ContextLinks title={meta.contextualLinksTitle}>
                {meta.contextualLinks.map(link => (
                  <AppLink href={link.url} key={link.url}>
                    {link.title}
                  </AppLink>
                ))}
              </ContextLinks>
            )}
          </SearchTitle>

          <div className={styles.tabs} role="navigation">
            <HorizontalTabs max={5}>
              {contentTypes.map(contentType => (
                <Tab
                  key={contentType.display}
                  href={contentType.href}
                  selected={contentType.selected}
                  disabled={contentType.disabled}
                  onClick={handleClickContentType}
                >
                  {contentType.display}
                </Tab>
              ))}
            </HorizontalTabs>
          </div>

          <CollectionsProvider>
            {!error && (
              <SearchFilters
                total={total}
                allFacets={getFacets(facets)}
                filter={filter}
                currentFilters={currentFilters}
                urlPath={urlPath}
                onFilter={handleClickSearchLink}
                selected={selected}
                unSelect={handleSelected}
                showOnlySelected={showOnlySelected}
                setShowOnlySelected={setShowOnlySelected}
              />
            )}

            <div className={clsx(styles.results, error && styles.hasError)}>
              {error && <SearchError href={urlPath} />}
              <DidYouMean
                suggestions={suggestions}
                onClick={handleClickSuggestion}
              />
              {!error && (
                <SearchResults
                  results={resultShown}
                  selected={selected}
                  handleSelected={handleSelected}
                  setShowOnlySelected={setShowOnlySelected}
                />
              )}
              {!error && (
                <Pagination
                  pages={pagesShown}
                  current={parseInt(filter.page)}
                  generateUrl={generatePaginatedUrl}
                  onClick={handlePagination}
                />
              )}
            </div>
          </CollectionsProvider>

          <div className={styles.clear} />

          {/*todo: only render for anon users*/}
          {filter.page === 1 && (
            <SearchFooter>
              <SearchFootnote
                title={meta.title}
                description={meta.description}
              />
              <SearchFootnote
                title={meta.bottomTitle}
                description={meta.bottomDescription}
              />
            </SearchFooter>
          )}
        </div>
      </div>
      <AdminLinks>
        <a href={`/secure/meta/searchpage.php?url=${urlPath}`} target="_blank">
          Edit Meta Info
        </a>
        <a href={`/secure/export-srp.php?path=${urlPath}`} target="_blank">
          Export Table
        </a>
      </AdminLinks>
    </div>
  );
}

Search.getInitialProps = async ({ asPath, req, res }) => {
  let result;

  const queryString = asPath
    // Just the querystring part
    .replace(/^[^?]+[?]?/, '')
    // Split into k=v parts
    .split('&')
    // Only include known search params
    .filter(val =>
      val.match(/^(page|sort|q|facets(\[|%5B)(types|grades)(\]|%5D))=/)
    )
    .join('&');

  // The url to search with (pathName + known query params)
  let searchUrl = asPath.replace(/\?.*$/, '');
  if (queryString.length) {
    searchUrl += '?' + decodeURIComponent(queryString);
  }

  try {
    if (req !== undefined) {
      result = await content(req).search(searchUrl, true);

      // If rendering on server-side, only allow valid urls and perform an actual redirect
      if ('redirect' in result) {
        let redirectUrl = result.redirect;
        // Persist some tracking querystring params when redirecting
        const additionalQuerystringParams = [];
        Object.keys(req.query).forEach(key => {
          if (key.match(/^(cid$|utm_)/) && req.query[key]) {
            additionalQuerystringParams.push(key + '=' + req.query[key]);
          }
        });
        if (additionalQuerystringParams.length) {
          redirectUrl +=
            (redirectUrl.match(/\?/) ? '&' : '?') +
            additionalQuerystringParams.join('&');
        }

        return res.redirect(301, redirectUrl);
      }
    } else {
      result = await content().search(searchUrl);

      // If rendering on client-side, update the url if the urlPath is different than the current path
      if (result.redirect) {
        result = Object.assign({}, result, { replaceUrl: true });
      }
    }
  } catch (e) {
    console.error(e);
    result = {
      urlPath: searchUrl,
      meta: {
        title: 'Search Our Content Library',
        metaTitle: 'Learning Library',
      },
      error: true,
    };
  }

  result.navProps = {
    page: 'search',
    withSubNav: false,
  };

  // We need the 'nesting' name of the contentType to match the other parts of the site for GA & GTM.
  let contentTypeNesting = null;
  if (result?.filter?.contentType) {
    contentTypeNesting = getContentTypes(result?.facets).filter(
      facet => facet.urlName === result.filter.contentType
    );
    contentTypeNesting = contentTypeNesting?.[0]?.['nesting'];
  }

  // Footer props for Tracking
  result.footerProps = {
    grades: result?.filter?.grade ? [result.filter.grade] : [],
    subjects: [
      getRootFacet(result?.filter?.subject, result?.facets, 'subject')?.urlName,
    ],
    contentType: contentTypeNesting,
    pageType: 'Search',
  };

  return result;
};

const contextualLinkPropTypes = {
  url: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

const metaPropTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  metaTitle: PropTypes.string.isRequired,
  metaDescription: PropTypes.string,
  bottomTitle: PropTypes.string,
  bottomDescription: PropTypes.string,
  contextualLinksTitle: PropTypes.string,
  contextualLinks: PropTypes.arrayOf(PropTypes.shape(contextualLinkPropTypes)),
  index: PropTypes.bool,
};

const filterPropTypes = {
  q: PropTypes.string,
  page: PropTypes.number,
  sort: PropTypes.string,
  grade: PropTypes.string,
  contentType: PropTypes.string,
  subject: PropTypes.string,
  topic: PropTypes.string,
  standard: PropTypes.string,
  el: PropTypes.string,
};

const facetChildrenPropTypes = {
  urlName: PropTypes.string.isRequired,
  nesting: PropTypes.string.isRequired,
  display: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  selected: PropTypes.bool.isRequired,
  count: PropTypes.number.isRequired,
};

const facetPropTypes = {
  internalName: PropTypes.string.isRequired,
  display: PropTypes.string.isRequired,
  children: PropTypes.arrayOf(
    PropTypes.shape(facetChildrenPropTypes).isRequired
  ),
};

const resultPropTypes = {
  type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  thumbnail: PropTypes.string,
  subject: PropTypes.string.isRequired,
  grade: PropTypes.string.isRequired,
  rating: PropTypes.number,
  new: PropTypes.bool.isRequired,
  answersheet: PropTypes.bool.isRequired,
  premium: PropTypes.bool,
  el: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  meta: PropTypes.object,
  isInteractive: PropTypes.bool,
};

const currentFiltersPropTypes = {
  display: PropTypes.string,
  group: PropTypes.string,
  href: PropTypes.string,
};

Search.propTypes = {
  total: PropTypes.number,
  pages: PropTypes.number,
  urlPath: PropTypes.string.isRequired,
  meta: PropTypes.shape(metaPropTypes).isRequired,
  filter: PropTypes.shape(filterPropTypes),
  currentFilters: PropTypes.arrayOf(PropTypes.shape(currentFiltersPropTypes)),
  facets: PropTypes.arrayOf(PropTypes.shape(facetPropTypes)),
  results: PropTypes.arrayOf(PropTypes.shape(resultPropTypes)),
  suggestions: PropTypes.arrayOf(PropTypes.string),
  replaceUrl: PropTypes.bool,
  error: PropTypes.bool,
};
